import { useAuth0 } from "@auth0/auth0-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Row as TanTableRow, createColumnHelper } from "@tanstack/react-table";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import {
  FieldError,
  FieldErrorsImpl,
  Merge,
  useFieldArray,
  useForm,
} from "react-hook-form";
import * as yup from "yup";

// SecurityType options that actually exist in the database
const securityTypeOptions = [
  "Alternative",
  "Bond",
  "Cash",
  "Cash Equivalent",
  "Crypto",
  "Equity",
  "ETF",
  "Fund",
  "Mutual Fund",
  "Stock",
  "Unknown",
];

interface MinimumTradeSize {
  id: number;
  firmId: number;
  securityType?: string | null;
  securitySymbol?: string | null;
  minimumAmount: number;
  created: string | Date;
  updated: string | Date;
  _isDeleted?: boolean;
  _isNew?: boolean;
}

/**
 * The route this screen READS the firm's minimum trade size rules from.
 *
 * Published at `/v1` by APP-1563, in the same change that paginated it — the
 * named change `docs/adr/firm-domain-admission.md` records against it on
 * criterion C8. It is also the FIRST element of every react-query key the walk
 * below uses, so the invalidation after a save reaches every page of the
 * collection rather than only the one whose url it happens to spell.
 *
 * The create, update and delete calls further down spell their own `/v1` paths
 * and are deliberately NOT written through this constant. They were versioned
 * by an earlier job, and each answers with a single rule rather than the
 * collection, so none of them is paginated and none moves when this one does.
 * The create posts to this very path — same string, different method — and
 * sharing the constant would tie a write's url to a read's pagination key for
 * no reason beyond the two happening to spell the same thing today.
 */
const MINIMUM_TRADE_SIZES_PATH = "/v1/firm/trades/minimum-sizes";

import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import FormError from "../components/FormError";
import FormFieldError from "../components/FormFieldError";
import SymbolField from "../components/SymbolField";
import TabContainerWithTabs from "../components/TabContainer";
import EditableTableRow, {
  EditableRowExtension,
} from "../components/Table/EditableTableRow";
import { Table, useTable } from "../components/Table/Table";
import { useAuthenticatedMutationAsync } from "../lib/api";
import { useRoles } from "../lib/auth";
import { formatCurrency } from "../lib/numbers";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";
import FirmNav from "./FirmNav";

type MinimumTradeSizeRow = MinimumTradeSize &
  EditableRowExtension & {
    _isDeleted?: boolean;
  };
type MinimumTradeSizeFields = {
  securityType?: string | null;
  securitySymbol?: string | null;
  minimumAmount: number;
};

const schema: yup.ObjectSchema<{
  minimumTradeSizes: MinimumTradeSizeFields[];
}> = yup.object({
  minimumTradeSizes: yup
    .array()
    .of(
      yup.object({
        securityType: yup.string().nullable().label("Security Type"),
        securitySymbol: yup.string().nullable().label("Security Symbol"),
        minimumAmount: yup
          .number()
          .required()
          .integer("Minimum amount must be a whole number")
          .min(0, "Minimum amount must be positive")
          .label("Minimum Amount"),
      }),
    )
    .required(),
});

const MinimumTradeSizes = () => {
  const roles = useRoles();
  const {
    control,
    register,
    reset,
    getValues,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });
  const { insert: insertField, remove: removeField } = useFieldArray({
    name: "minimumTradeSizes",
    control,
    keyName: "_field_id",
  });

  const [minimumTradeSizesEdited, setMinimumTradeSizes] = useState<
    MinimumTradeSize[]
  >([]);
  const [changedRules, setChangedRules] = useState<
    Record<string, MinimumTradeSize>
  >({});

  const handleUpdateMinimumTradeSize = useCallback(
    (
      index: number,
      securityType: string | null,
      securitySymbol: string | null,
      minimumAmount: number,
    ) => {
      setMinimumTradeSizes(
        minimumTradeSizesEdited.map((rule, indexInner) => {
          if (index !== indexInner) {
            return rule;
          }

          const updatedRule = {
            ...rule,
            securityType,
            securitySymbol,
            minimumAmount,
            // Keep the original _isNew value - don't override it
          };

          setChangedRules((changedRules) => ({
            ...changedRules,
            [rule.id]: updatedRule,
          }));

          return updatedRule;
        }),
      );
    },
    [minimumTradeSizesEdited],
  );

  const onConfirmRow = useCallback(
    (row: TanTableRow<MinimumTradeSizeRow>) => {
      const vals = getValues(`minimumTradeSizes.${row.index}`);
      handleUpdateMinimumTradeSize(
        row.index,
        vals.securityType || null,
        vals.securitySymbol || null,
        vals.minimumAmount,
      );
    },
    [getValues, handleUpdateMinimumTradeSize],
  );

  const onEditRow = useCallback(
    (row: TanTableRow<MinimumTradeSizeRow>) => {
      setValue(`minimumTradeSizes.${row.index}`, row.original);
    },
    [setValue],
  );

  const onDiscardRow = useCallback(
    (row: TanTableRow<MinimumTradeSizeRow>) => {
      setValue(`minimumTradeSizes.${row.index}`, row.original);
    },
    [setValue],
  );

  const onAddRow = useCallback(() => {
    const newRow = {
      id: Date.now(), // temporary ID for new rows
      firmId: 0, // will be set by backend
      securityType: null,
      securitySymbol: null,
      minimumAmount: 0,
      created: new Date().toISOString(),
      updated: new Date().toISOString(),
      _isNew: true,
    };

    insertField(0, newRow, { shouldFocus: true });
    setMinimumTradeSizes([newRow, ...minimumTradeSizesEdited]);
  }, [insertField, minimumTradeSizesEdited]);

  const onRemoveRow = useCallback(
    (row: TanTableRow<MinimumTradeSizeRow>) => {
      removeField(row.index);
      setMinimumTradeSizes(
        minimumTradeSizesEdited.filter((rule, index) => index !== row.index),
      );

      if (row.original._isNew) {
        delete changedRules[row.original.id];
        setChangedRules(changedRules);
      } else {
        // Mark for deletion
        setChangedRules({
          ...changedRules,
          [row.original.id]: { ...row.original, _isDeleted: true },
        });
      }
    },
    [changedRules, removeField, minimumTradeSizesEdited],
  );

  const securityTypeSelectOptions = useMemo(
    () => [
      <option key="" value="">
        -
      </option>,
      ...securityTypeOptions
        .filter((type) => type !== "") // Remove empty string since we have firm-wide option
        .sort()
        .map((securityType) => (
          <option key={securityType} value={securityType}>
            {securityType}
          </option>
        )),
    ],
    [],
  );

  const columnHelper = useMemo(
    () => createColumnHelper<MinimumTradeSizeRow>(),
    [],
  );

  const tradeSizeErrors = errors.minimumTradeSizes as Merge<
    FieldError,
    (Merge<FieldError, FieldErrorsImpl<MinimumTradeSizeRow>> | undefined)[]
  >;

  const columns = useMemo(
    () => [
      columnHelper.accessor("securityType", {
        header: () => "Security Type",
        cell: (info) => {
          const value = info.getValue();
          return value || "-";
        },
        filterFn: (row, columnId, filterValue) => {
          const value = row.getValue(columnId) as string;
          if (!filterValue) return true;
          if (!value)
            return "-".toLowerCase().includes(filterValue.toLowerCase());
          return value.toLowerCase().includes(filterValue.toLowerCase());
        },
        meta: {
          editableComponent: (cell) => {
            const symbolValue = getValues(
              `minimumTradeSizes.${cell.row.index}.securitySymbol`,
            );
            const isTypeDisabled = Boolean(symbolValue);

            return (
              <>
                <Form.Select
                  {...register(
                    `minimumTradeSizes.${cell.row.index}.securityType`,
                  )}
                  disabled={isTypeDisabled}
                  onChange={(e) => {
                    // Clear security symbol when type is selected
                    if (e.target.value) {
                      setValue(
                        `minimumTradeSizes.${cell.row.index}.securitySymbol`,
                        "",
                      );
                    }
                    // Let react-hook-form handle the type value
                    register(
                      `minimumTradeSizes.${cell.row.index}.securityType`,
                    ).onChange(e);
                  }}
                >
                  {securityTypeSelectOptions}
                </Form.Select>
                <FormFieldError
                  field={tradeSizeErrors?.[cell.row.index]?.securityType}
                />
                {isTypeDisabled && (
                  <Form.Text className="text-muted">
                    Disabled when security symbol is selected
                  </Form.Text>
                )}
              </>
            );
          },
        },
      }),
      columnHelper.accessor("securitySymbol", {
        header: () => "Security Symbol",
        cell: (info) => info.getValue() || "-",
        meta: {
          editableComponent: (cell) => {
            const typeValue = getValues(
              `minimumTradeSizes.${cell.row.index}.securityType`,
            );
            const isDisabled = Boolean(typeValue);

            return (
              <>
                <SymbolField
                  fieldName={`minimumTradeSizes.${cell.row.index}.securitySymbol`}
                  register={register}
                  control={control}
                  getValues={getValues}
                  disabled={isDisabled}
                  onSelect={(suggestion) => {
                    // Clear security type when symbol is selected
                    if (suggestion.symbol) {
                      setValue(
                        `minimumTradeSizes.${cell.row.index}.securityType`,
                        "",
                      );
                    }
                  }}
                />
                <FormFieldError
                  field={tradeSizeErrors?.[cell.row.index]?.securitySymbol}
                />
                {isDisabled && (
                  <Form.Text className="text-muted">
                    Disabled when security type is selected
                  </Form.Text>
                )}
              </>
            );
          },
        },
      }),
      columnHelper.accessor((row) => row.minimumAmount ?? 0, {
        id: "minimumAmount",
        header: () => "Minimum Amount",
        cell: (info) => formatCurrency(info.getValue(), 0),
        meta: {
          editableComponent: (cell) => (
            <>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                {...register(
                  `minimumTradeSizes.${cell.row.index}.minimumAmount`,
                  { valueAsNumber: true },
                )}
              />
              <FormFieldError
                field={tradeSizeErrors?.[cell.row.index]?.minimumAmount}
              />
            </>
          ),
        },
      }),
      columnHelper.accessor("updated", {
        cell: (info) => {
          const value = info.getValue();
          if (!value) return "-";
          const date = typeof value === "string" ? new Date(value) : value;
          return date.toLocaleDateString();
        },
        header: () => "Updated",
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: "actions",
        size: 120,
      }),
      columnHelper.display({
        id: "add-remove",
      }),
    ],
    [
      columnHelper,
      register,
      control,
      getValues,
      setValue,
      tradeSizeErrors,
      securityTypeSelectOptions,
    ],
  );

  // The route answers a PAGE, not the whole collection (APP-1563), so one
  // response is never the answer on its own: the walk in
  // `useAuthenticatedFetchAllPages` keeps asking until it has as many rules as
  // the sibling key says the firm has. Nothing in `tsc` can catch getting this
  // wrong — the element type of a page and of the whole array are the same — so
  // `MinimumTradeSizes.pagination.spec.tsx` is what holds it.
  const {
    rows: minimumTradeSizes,
    hasEveryRow: hasEveryMinimumTradeSize,
    isPendingFirstPage: isPendingFirstMinimumTradeSizesPage,
    isError: isErrorMinimumTradeSizes,
    reload: reloadMinimumTradeSizes,
  } = useAuthenticatedFetchAllPages<MinimumTradeSize>(MINIMUM_TRADE_SIZES_PATH);

  useEffect(() => {
    // Copied into the editable state only once the walk HOLDS every rule. A
    // copy per page would hand the screen a partial collection to edit, and
    // `reset` would then throw away an edit made to a row the walk had already
    // delivered every time the next page landed.
    if (hasEveryMinimumTradeSize) {
      reset({ minimumTradeSizes });
      setMinimumTradeSizes(minimumTradeSizes);
    }
  }, [hasEveryMinimumTradeSize, minimumTradeSizes, reset]);

  const { table } = useTable({
    columns: columns,
    data: minimumTradeSizesEdited,
    initialState: {
      pagination: {
        pageSize: 40,
      },
    },
    autoResetAll: false,
  });

  const createMutation = useAuthenticatedMutationAsync<{
    data: MinimumTradeSize;
  }>(
    "/v1/firm/trades/minimum-sizes",
    async (
      data: Omit<
        MinimumTradeSize,
        "id" | "firmId" | "created" | "updated" | "_isNew" | "_isDeleted"
      >,
    ) => ({
      method: "POST",
      body: JSON.stringify(data),
    }),
  );

  const { getAccessTokenSilently } = useAuth0();

  const updateRule = useCallback(
    async (rule: MinimumTradeSize) => {
      const { firmId, created, updated, _isNew, _isDeleted, ...updateData } =
        rule;
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT}/v1/firm/trades/minimum-sizes/${rule.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updateData),
        },
      );
      if (!response.ok) throw new Error("Failed to update rule");
      return response.json();
    },
    [getAccessTokenSilently],
  );

  const deleteRule = useCallback(
    async (id: number) => {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT}/v1/firm/trades/minimum-sizes/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) throw new Error("Failed to delete rule");
      return response.json();
    },
    [getAccessTokenSilently],
  );

  const handleDiscardChanges = useCallback(() => {
    setMinimumTradeSizes(minimumTradeSizes);
    reset({ minimumTradeSizes });
    setChangedRules({});
  }, [reset, minimumTradeSizes]);

  const queryClient = useQueryClient();
  const notificationContext = useContext(NotificationContext);

  const onSubmit = useMutation({
    mutationFn: async () => {
      if (isValid) {
        try {
          // Process each changed rule
          for (const rule of Object.values(changedRules)) {
            if (rule._isDeleted) {
              await deleteRule(rule.id);
            } else if (rule._isNew) {
              const {
                id,
                firmId,
                created,
                updated,
                _isNew,
                _isDeleted,
                ...createData
              } = rule;
              await createMutation(createData);
            } else {
              await updateRule(rule);
            }
          }

          // The read is a walk over pages now (APP-1563), so its cache holds
          // one entry per page keyed `[path, offset]`. Invalidating the PATH
          // matches every one of them; the old key named the unversioned path
          // this screen no longer reads and would match nothing.
          //
          // The collected pages are dropped as well, and that half is not
          // optional: the walk stops asking once it holds the whole
          // collection, so an invalidation on its own would mark entries stale
          // that nothing is subscribed to any more and refetch nothing at all.
          // Dropping them restarts the walk at offset 0 against the entries
          // just invalidated.
          queryClient.invalidateQueries({
            queryKey: [MINIMUM_TRADE_SIZES_PATH],
          });
          reloadMinimumTradeSizes();
          setChangedRules({});
          notificationContext.pushNotification({
            id: "minimum-trade-sizes",
            header: "Minimum Trade Sizes Updated",
            body: "Minimum trade size rules updated successfully",
            variant: "success",
          });
        } catch (err) {
          console.error("Failed to save minimum trade sizes", err);
          notificationContext.pushNotification({
            id: "minimum-trade-sizes",
            header: "Failed to Save Minimum Trade Sizes",
            body: "Minimum trade size rules not saved",
            variant: "danger",
          });
        }
      }
    },
  });

  return (
    <TabContainerWithTabs tabs={FirmNav}>
      {/* Only while the FIRST page is outstanding. Every further page turns the
          hook pending again under a new key, and unmounting the table for each
          of them would flash the screen through Loading once per page. */}
      {isPendingFirstMinimumTradeSizesPage ? (
        <Loading />
      ) : isErrorMinimumTradeSizes ? (
        <Alert variant="danger">
          Failed to retrieve minimum trade size rules
        </Alert>
      ) : (
        <>
          <Row className="mb-3">
            <Col>
              <h4>Minimum Trade Size Configuration</h4>
              <p className="text-muted">
                Configure minimum trade sizes for trade proposals. Rules are
                applied hierarchically: security-specific → type-specific →
                firm-wide. There can be only one firm-wide.
              </p>
            </Col>
          </Row>
          {!roles.includes("Admin") ||
          Object.keys(changedRules).length <= 0 ? null : (
            <Row className="mb-2">
              <Col className="d-grid gap-1" xl={6} md={12}>
                <Button
                  className="mb-1"
                  variant="success"
                  onClick={() => onSubmit.mutateAsync()}
                  disabled={onSubmit.isPending}
                >
                  Submit{" "}
                  {onSubmit.isPending ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    ""
                  )}
                </Button>
              </Col>
              <Col className="d-grid gap-1" xl={6} md={12}>
                <Button
                  className="mb-1"
                  variant="danger"
                  onClick={handleDiscardChanges}
                  disabled={onSubmit.isPending}
                >
                  Discard
                </Button>
              </Col>
            </Row>
          )}
          <Row>
            <Col
              style={{ pointerEvents: onSubmit.isPending ? "none" : "auto" }}
            >
              <FormError
                hasError={!isValid}
                message="Please fix the errors above."
              />
              <Table
                table={table}
                RowComp={roles.includes("Admin") ? EditableTableRow : undefined}
                onSave={onConfirmRow}
                onEdit={onEditRow}
                onDiscard={onDiscardRow}
                enableAddRemove
                onAdd={onAddRow}
                onRemove={onRemoveRow}
              />
            </Col>
          </Row>
        </>
      )}
    </TabContainerWithTabs>
  );
};

export default MinimumTradeSizes;
